import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Layers, Lock, ArrowRight, FileText, Building2, Cpu,
  Download, Eye, Image, ZoomIn, ZoomOut, RotateCw, RefreshCw,
  ChevronLeft, ChevronRight, Shield
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ConfidenceBadge({ value }) {
  const cls = value >= 0.9
    ? 'bg-emerald-950/30 text-emerald-400 border-emerald-900'
    : value >= 0.75
    ? 'bg-amber-950/30 text-amber-400 border-amber-900'
    : 'bg-rose-950/30 text-rose-400 border-rose-900';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border ${cls}`}>
      {Math.round(value * 100)}%
    </span>
  );
}

export default function PortalPage() {
  const { shareToken } = useParams();
  const [stage, setStage] = useState('verify'); // verify | portal
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [portalToken, setPortalToken] = useState('');
  const [clientName, setClientName] = useState('');

  // Portal state
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetImage, setSheetImage] = useState(null);
  const [imageVersion, setImageVersion] = useState('annotated');
  const [imageLoading, setImageLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [activeTab, setActiveTab] = useState('spaces');
  const [downloading, setDownloading] = useState(false);

  // Verify access code
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);
    try {
      const res = await axios.post(`${API_URL}/portal/${shareToken}/verify`, {
        access_code: accessCode.toUpperCase(),
      });
      setPortalToken(res.data.portal_token);
      setClientName(res.data.client_name || '');
      setStage('portal');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      setError(detail);
    } finally {
      setVerifying(false);
    }
  };

  // Fetch portal data
  const fetchData = useCallback(async () => {
    if (!portalToken) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/portal/${shareToken}/data?portal_token=${portalToken}`
      );
      setPortalData(res.data);
      if (res.data.sheets?.length > 0) {
        setSelectedSheet(res.data.sheets[0]);
      }
    } catch (err) {
      console.error('Failed to load portal data:', err);
      if (err.response?.status === 401) {
        setStage('verify');
        setError('Session expired. Please re-enter your access code.');
      }
    } finally {
      setLoading(false);
    }
  }, [shareToken, portalToken]);

  useEffect(() => {
    if (stage === 'portal') fetchData();
  }, [stage, fetchData]);

  // Fetch sheet image
  const fetchSheetImage = useCallback(async () => {
    if (!selectedSheet || !portalToken) return;
    setImageLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/portal/${shareToken}/sheet-image?portal_token=${portalToken}&sheet_id=${selectedSheet.id}&version=${imageVersion}`
      );
      setSheetImage(res.data);
    } catch (err) {
      console.error('Failed to load image:', err);
      setSheetImage(null);
    } finally {
      setImageLoading(false);
    }
  }, [shareToken, portalToken, selectedSheet, imageVersion]);

  useEffect(() => { fetchSheetImage(); }, [fetchSheetImage]);

  // Download package
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API_URL}/portal/${shareToken}/download-package?portal_token=${portalToken}`
      );
      const byteChars = atob(res.data.data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename || 'delivery.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  // ---- ACCESS CODE SCREEN ----
  if (stage === 'verify') {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center p-4" data-testid="portal-verify-page">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center gap-2 mb-8 justify-center">
            <Layers className="w-8 h-8 text-primary" />
            <span className="font-heading font-bold text-xl tracking-tight text-white">
              FMiQ<span className="text-primary">Intel</span>
            </span>
          </div>

          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-violet-400" />
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-white">
                Client Delivery Portal
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              Enter your access code to view the project drawings and data.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-rose-950/30 border border-rose-800/50 text-rose-400 text-xs font-mono rounded-sm" data-testid="portal-error">
                {error}
              </div>
            )}

            <form onSubmit={handleVerify}>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Access Code
              </label>
              <input
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                data-testid="access-code-input"
                className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-3 font-mono text-lg text-center text-white placeholder:text-muted-foreground outline-none transition-colors tracking-[0.5em] uppercase"
                placeholder="------"
                maxLength={6}
                autoFocus
              />
              <button
                type="submit"
                disabled={accessCode.length < 6 || verifying}
                data-testid="verify-code-btn"
                className="w-full flex items-center justify-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 transition-colors disabled:opacity-50 mt-4"
              >
                {verifying ? 'Verifying...' : 'Access Documents'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          <p className="text-center text-[10px] text-muted-foreground mt-4 font-mono">
            Secured by FMiQ Drawing Intelligence Engine
          </p>
        </div>
      </div>
    );
  }

  // ---- PORTAL VIEW ----
  if (loading || !portalData) {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-card" data-testid="portal-main">
      {/* Header */}
      <div className="h-14 bg-card border-b border-border flex items-center justify-between px-5 shrink-0">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-primary" />
          <div>
            <h1 className="font-heading font-bold text-sm tracking-tight uppercase text-white">
              {portalData.project_name}
            </h1>
            <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
              <span>{portalData.facility_key}</span>
              <span>{portalData.revision}</span>
              {clientName && <span>for {clientName}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-4 text-[10px] font-mono text-muted-foreground">
            <span><Building2 className="w-3 h-3 inline mr-1" />{portalData.statistics?.total_spaces} spaces</span>
            <span><Cpu className="w-3 h-3 inline mr-1" />{portalData.statistics?.total_assets} assets</span>
          </div>
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="portal-download-btn"
            className="flex items-center gap-2 border-l-2 border-violet-500 bg-violet-600/10 hover:bg-violet-600/20 text-violet-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? 'Preparing...' : 'Download Package'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left - Drawing Viewer */}
        <div className="flex-1 bg-card flex flex-col border-r border-border">
          {/* Sheet tabs */}
          <div className="h-10 bg-card border-b border-border flex items-center px-3 gap-2 overflow-x-auto shrink-0">
            {portalData.sheets.map((sheet) => (
              <button
                key={sheet.id}
                onClick={() => { setSelectedSheet(sheet); setZoom(1); }}
                data-testid={`portal-sheet-${sheet.id}`}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-mono whitespace-nowrap transition-colors ${
                  selectedSheet?.id === sheet.id
                    ? 'bg-primary/10 text-primary border border-primary/30'
                    : 'text-muted-foreground hover:text-foreground/80 hover:bg-muted/50'
                }`}
              >
                <FileText className="w-3 h-3" />
                {sheet.sheet_number}
              </button>
            ))}
          </div>

          {/* Version + Zoom */}
          <div className="h-9 bg-card/80 border-b border-border/50 flex items-center justify-between px-3 shrink-0">
            <div className="flex gap-1">
              {['annotated', 'clean', 'enhanced', 'original'].map((v) => (
                <button
                  key={v}
                  onClick={() => setImageVersion(v)}
                  data-testid={`portal-version-${v}`}
                  className={`px-2 py-1 rounded text-[10px] font-heading uppercase tracking-wider transition-colors ${
                    imageVersion === v
                      ? 'bg-blue-600/20 text-blue-400 border border-blue-800'
                      : 'text-muted-foreground hover:text-foreground/80'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} className="p-1 hover:bg-secondary rounded text-muted-foreground">
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="font-mono text-[10px] text-muted-foreground w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="p-1 hover:bg-secondary rounded text-muted-foreground">
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setZoom(1)} className="p-1 hover:bg-secondary rounded text-muted-foreground ml-1">
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4">
            {imageLoading ? (
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            ) : sheetImage?.image ? (
              <img
                src={`data:image/${sheetImage.format || 'png'};base64,${sheetImage.image}`}
                alt="Drawing"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center', maxWidth: 'none' }}
                className="transition-transform duration-200"
                data-testid="portal-sheet-image"
              />
            ) : (
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Image className="w-8 h-8" />
                <p className="text-xs font-mono">No image available for this version</p>
              </div>
            )}
          </div>
        </div>

        {/* Right - Data Panel */}
        <div className="w-[380px] xl:w-[440px] bg-card flex flex-col shrink-0">
          <div className="h-10 border-b border-border flex items-center px-4">
            <span className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground">Project Data</span>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border px-2">
            {[
              { key: 'spaces', label: 'Spaces', count: portalData.spaces?.length || 0 },
              { key: 'assets', label: 'Assets', count: portalData.asset_candidates?.length || 0 },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`portal-tab-${tab.key}`}
                className={`px-3 py-2 font-heading text-[10px] uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground/80'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Data */}
          <div className="flex-1 overflow-auto">
            {activeTab === 'spaces' && (
              <div className="divide-y divide-slate-800/30">
                {(portalData.spaces || []).map((space) => (
                  <div key={space.id} className="px-4 py-2.5 hover:bg-secondary/20 transition-colors" data-testid={`portal-space-${space.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-foreground font-medium">{space.room_number}</span>
                        <span className="font-mono text-xs text-muted-foreground">{space.room_name}</span>
                      </div>
                      <ConfidenceBadge value={space.confidence} />
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground mt-0.5">
                      <span>Level {space.level_key}</span>
                      {space.area && <span>{space.area} sf</span>}
                      {space.department && <span>{space.department}</span>}
                      <span className={`uppercase ${space.status === 'approved' ? 'text-emerald-500' : space.status === 'rejected' ? 'text-rose-500' : 'text-muted-foreground'}`}>
                        {space.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'assets' && (
              <div className="divide-y divide-slate-800/30">
                {(portalData.asset_candidates || []).map((asset) => (
                  <div key={asset.id} className="px-4 py-2.5 hover:bg-secondary/20 transition-colors" data-testid={`portal-asset-${asset.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary font-medium">{asset.tag}</span>
                        <span className="font-mono text-xs text-muted-foreground">{asset.asset_type}</span>
                      </div>
                      <ConfidenceBadge value={asset.confidence} />
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground mt-0.5">
                      <span>Room: {asset.room_number || '-'}</span>
                      {asset.manufacturer && <span>{asset.manufacturer}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
